import React, { useCallback, useEffect, useState } from 'react';
import { blogStagesService, BlogStage } from '../../../../services/blogStagesService';
import { DataTable, DataTableColumn } from '../../shared/DataTable';

// ---------------------------------------------------------------------------
// Prompt List
// ---------------------------------------------------------------------------

type StageRow = BlogStage & { key: string };

const COLUMNS: DataTableColumn<StageRow>[] = [
  {
    key: 'stage',
    header: 'Stage',
    render: (row) => {
      const stageNumber = row.stage_id.replace('stage_', '').split('_')[0];
      const stageName = (row.name || row.stage_id).replace(/^Stage \d+[a-z]?:\s*/, '');
      return (
        <>
          <span className="font-mono text-xs text-gray-400 mr-2">{stageNumber}</span>
          <span className="font-medium text-gray-900">{stageName}</span>
        </>
      );
    },
  },
  {
    key: 'model',
    header: 'Model',
    render: (row) =>
      row.prompt_template ? (
        <span className="text-gray-500">{row.model || '—'}</span>
      ) : (
        <span className="text-gray-300 italic">(non-LLM)</span>
      ),
  },
  {
    key: 'updated',
    header: 'Updated',
    render: (row) => (
      <span className="text-gray-500">
        {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '—'}
      </span>
    ),
  },
];

function PromptList({ onSelect }: { onSelect: (stage: BlogStage) => void }) {
  const [stages, setStages] = useState<StageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await blogStagesService.list('active');
        const sorted = (resp.stages || [])
          .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
          .map((s) => ({ ...s, key: s.stage_id }));
        setStages(sorted);
      } catch (e: any) {
        setError(e?.message || 'Failed to load stages');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }
  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>;
  }

  return (
    <DataTable
      columns={COLUMNS}
      rows={stages}
      onRowClick={(row) => onSelect(row)}
      isRowClickable={(row) => !!row.prompt_template}
      emptyMessage="No stages found."
    />
  );
}

// ---------------------------------------------------------------------------
// Global Prompt Editor
// ---------------------------------------------------------------------------

function GlobalPromptEditor({ stage, onBack, onSaved }: {
  stage: BlogStage;
  onBack: () => void;
  onSaved: () => void;
}) {
  const [template, setTemplate] = useState(stage.prompt_template || '');
  const [systemPrompt, setSystemPrompt] = useState(stage.system_prompt || '');
  const [model, setModel] = useState(stage.model || '');
  const [temperature, setTemperature] = useState(stage.temperature ?? 0.2);
  const [maxTokens, setMaxTokens] = useState(stage.max_tokens ?? 4000);
  const [thinkingEnabled, setThinkingEnabled] = useState(!!stage.thinking);
  const [effort, setEffort] = useState<'low' | 'medium' | 'high'>(stage.effort ?? 'medium');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  const stageName = (stage.name || stage.stage_id).replace(/^Stage \d+[a-z]?:\s*/, '');

  const templateVars = Array.from(new Set(
    (template || '').match(/\{\{\s*[\w.]+\s*\}\}/g)?.map(v => v.replace(/[{}\s]/g, '')) || []
  ));

  const isDirty = template !== (stage.prompt_template || '') ||
    systemPrompt !== (stage.system_prompt || '') ||
    model !== (stage.model || '') ||
    (!thinkingEnabled && temperature !== (stage.temperature ?? 0.2)) ||
    maxTokens !== (stage.max_tokens ?? 4000) ||
    thinkingEnabled !== !!stage.thinking ||
    (thinkingEnabled && effort !== (stage.effort ?? 'medium'));

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const thinking = thinkingEnabled ? { type: 'adaptive' as const } : null;
      const savedEffort = thinkingEnabled ? effort : null;
      await blogStagesService.update(stage.stage_id, {
        prompt_template: template,
        system_prompt: systemPrompt || null,
        model,
        temperature: thinkingEnabled ? null : temperature,
        max_tokens: maxTokens,
        thinking,
        effort: savedEffort,
      } as any);
      setSavedMessage('Saved successfully');
      onSaved();
      stage.prompt_template = template;
      stage.system_prompt = systemPrompt || null;
      stage.model = model;
      stage.temperature = thinkingEnabled ? null : temperature;
      stage.max_tokens = maxTokens;
      stage.thinking = thinking;
      stage.effort = savedEffort;
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
      setShowConfirm(false);
    }
  }, [template, systemPrompt, model, temperature, maxTokens, thinkingEnabled, effort, stage, onSaved]);

  const handleReset = () => {
    setTemplate(stage.prompt_template || '');
    setSystemPrompt(stage.system_prompt || '');
    setModel(stage.model || '');
    setTemperature(stage.temperature ?? 0.2);
    setMaxTokens(stage.max_tokens ?? 4000);
    setThinkingEnabled(!!stage.thinking);
    setEffort(stage.effort ?? 'medium');
    setError(null);
    setSavedMessage(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-3 inline-block">
          &larr; Back to prompts
        </button>
        <h2 className="text-lg font-semibold text-gray-900">{stageName}</h2>
        {stage.description && <p className="text-sm text-gray-500 mt-1">{stage.description}</p>}
      </div>

      <div className="bg-white border rounded p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Settings</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
            <input type="text" value={model} onChange={(e) => setModel(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Max Tokens</label>
            <input type="number" step="100" min="100" value={maxTokens}
              onChange={(e) => setMaxTokens(parseInt(e.target.value) || 4000)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div />
        </div>
        <div className="grid grid-cols-3 gap-4 mt-3 items-end">
          <div>
            <label className="flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer">
              <input type="checkbox" checked={thinkingEnabled} onChange={(e) => setThinkingEnabled(e.target.checked)}
                className="rounded border-gray-300" />
              Extended Thinking
            </label>
          </div>
          {thinkingEnabled ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Effort</label>
              <select value={effort} onChange={(e) => setEffort(e.target.value as 'low' | 'medium' | 'high')}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Temperature</label>
              <input type="number" step="0.1" min="0" max="2" value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          )}
          {thinkingEnabled && (
            <div className="text-xs text-gray-400 italic pb-2">Temperature is dropped when thinking is on. Thinking tokens count against Max Tokens — leave headroom. Effort is not supported on Haiku models.</div>
          )}
        </div>
      </div>

      <div className="bg-white border rounded p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">System Prompt</h3>
        <textarea value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} rows={3}
          placeholder="Optional system prompt..."
          className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y" />
      </div>

      <div className="bg-white border rounded p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Prompt Template</h3>
        <textarea value={template} onChange={(e) => setTemplate(e.target.value)} rows={20}
          className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y" />
      </div>

      {templateVars.length > 0 && (
        <details className="bg-white border rounded p-4">
          <summary className="text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer">
            Template Variables ({templateVars.length})
          </summary>
          <div className="mt-3 space-y-1">
            {templateVars.map(v => (
              <div key={v} className="text-sm font-mono text-gray-600 py-0.5">
                {'{{ '}{v}{' }}'}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex items-center gap-3">
        <button onClick={() => setShowConfirm(true)} disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={handleReset} disabled={!isDirty}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
          Discard changes
        </button>
        {savedMessage && <span className="text-sm text-green-600">{savedMessage}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>Changes to this template affect all future articles. Articles with per-article prompt overrides will not be affected.</span>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" onClick={() => setShowConfirm(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Save default template?</h3>
            <p className="text-sm text-gray-600">
              This will change the default prompt template for all future articles. Articles with per-article overrides will not be affected.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700" onClick={handleSave}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section root
// ---------------------------------------------------------------------------

export const PromptManagement: React.FC = () => {
  const [selectedStage, setSelectedStage] = useState<BlogStage | null>(null);
  const [listKey, setListKey] = useState(0);

  if (selectedStage) {
    return (
      <GlobalPromptEditor
        stage={selectedStage}
        onBack={() => setSelectedStage(null)}
        onSaved={() => setListKey(k => k + 1)}
      />
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-900 mb-6">Prompt Templates</h1>
      <p className="text-sm text-gray-500 mb-4">
        Edit the default Jinja2 prompt templates used by each pipeline stage. Per-article overrides (set within an article's stage panel) take precedence over these defaults.
      </p>
      <PromptList key={listKey} onSelect={setSelectedStage} />
    </div>
  );
};
